/**
 * Spec 179 (CC-54134): the exact sum behind a client export's totals row.
 *
 * ## The defect
 *
 * The client CSV / XLSX / PDF writers re-add every SUM-only column over the rows
 * they export (`exportClientView.js`, CC-49457). That re-add was a plain JavaScript
 * `+=` over doubles, and a double cannot hold most cent amounts exactly, so the sum
 * of 2,068 two-decimal amounts lands a hair off the decimal answer. Papaparse writes
 * a number with `String(n)`, which prints that hair, and ExcelJS stores it:
 *
 * | live run (QA vladdemo, 2026-10-15)    | CB's footer    | the file's total       |
 * | ------------------------------------- | -------------- | ---------------------- |
 * | 155 WIP - AR - Trust, Fee WIP         | 398,261.96     | `398261.95999999996`   |
 * | 155 WIP - AR - Trust, Trust Bal       | 7,854,089.45   | `7854089.450000001`    |
 * | AP 37 Detail As Of 09/28/2026, Total  | 619,989.80     | `619989.7999999999`    |
 *
 * Every row cell in those files was clean: each one is a single value CB sent, and
 * `String` prints the shortest decimal that reads back as that double, which is CB's.
 *
 * ## The fix
 *
 * Add the decimals, not the doubles. Each addend is read as the decimal `String`
 * prints for it (the value CB sent), the addends are summed as integers at the
 * finest scale any of them carries, and the result is converted to a number once.
 *
 * So the total is exactly the sum of the figures in the file — at THEIR precision,
 * which is the column's real precision. Nothing is rounded to a display precision:
 * two-decimal amounts total to cents (619989.8), a one-decimal hours column to one
 * decimal (4324.2), counts to an integer, and a column whose rows carry eight
 * decimals totals to eight. Rounding a file total to what the screen DISPLAYS would
 * break the file's own arithmetic against its unrounded rows (cb2 CLAUDE.md: round
 * the model's copy, never the export's).
 *
 * Integer arithmetic on BigInt has no accumulated error however many rows there are,
 * so this is not a "round to 15 significant digits" heuristic, which a long enough
 * column of cents defeats. The one limit is the final conversion: a total with more
 * than 15 significant digits (a money total of ten trillion and up) becomes the
 * nearest double, the most any numeric cell, and Excel, can hold.
 */

// The forms `String(n)` gives a finite number: "12", "-0.5", "1e+21", "1.5e-7".
const DECIMAL_TEXT = /^(-?)(\d+)(?:\.(\d+))?(?:e([+-]?\d+))?$/i

/**
 * A finite number as the exact decimal `String` prints for it:
 * `{ units, scale }` with value = units / 10^scale, units a BigInt. Null for
 * anything that is not a finite number.
 */
export const exactDecimal = (n) => {
  if (typeof n !== 'number' || !Number.isFinite(n)) return null
  const m = DECIMAL_TEXT.exec(String(n))
  if (!m) return null
  const [, sign, whole, frac = '', exp = '0'] = m
  let units = BigInt(whole + frac)
  let scale = frac.length - Number(exp)
  if (scale < 0) {
    units *= 10n ** BigInt(-scale)
    scale = 0
  }
  return { units: sign ? -units : units, scale }
}

/**
 * units / 10^scale as a number: the nearest double to that exact decimal. A sum
 * that cancels is 0n, which is not negative, so it is 0 — never -0.
 */
const toNumberAt = (units, scale) => {
  const negative = units < 0n
  const digits = (negative ? -units : units).toString().padStart(scale + 1, '0')
  const text = scale > 0 ? `${digits.slice(0, -scale)}.${digits.slice(-scale)}` : digits
  return Number(`${negative ? '-' : ''}${text}`)
}

/**
 * The exact decimal sum of the finite numbers in `values` (anything else is
 * skipped, exactly as the totals row always skipped it), or null when there are
 * none — so a column with nothing to add stays blank rather than reading 0.
 */
export const exactDecimalSum = (values) => {
  const parts = []
  let scale = 0
  for (const v of values || []) {
    const d = exactDecimal(v)
    if (!d) continue
    parts.push(d)
    if (d.scale > scale) scale = d.scale
  }
  if (parts.length === 0) return null
  let units = 0n
  for (const p of parts) units += p.units * 10n ** BigInt(scale - p.scale)
  return toNumberAt(units, scale)
}
